/**
 * Map Component
 */

import { v4 as uuidv4 } from 'uuid'

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseCornerRadiusFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatFromVariableSelect,
  formatSpacingFormat,
  formatWidthFormat,
  formatComponentAlignmentFormat,
  formatResponsiveCornerRadiusFormat,
  formatResponsiveValue,
  formatColorVariantFormat,
} from '../../format-utils'
import mapTemplate from './map.template.json'

export interface IMapComponentSettings {
  // Data settings
  'location-data-address': VariableSelectValue
  'location-data-marker': VariableSelectValue
  'location-data-header': VariableTextValue
  'location-data-subheader': VariableTextValue
  'location-data-description': VariableTextValue
  'location-data-avatar': VariableSelectValue
  'location-data-image': VariableSelectValue

  // Design settings
  'location-design-scheme': ColorAdaptiveValue<ColorVariant>
  'location-design-color-marker': ColorAdaptiveValue<ColorVariant>
  'location-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'location-design-height': ResponsiveValue<number[]>
  'location-design-alignment': ResponsiveValue<ComponentAlignment>
  'location-design-shape': ResponsiveValue<string>
  'location-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'location-design-zoom-level': string

  // Behavior settings
  'location-behavior-map-type-control': boolean
  'location-behavior-fullscreen-control': boolean
  'location-behavior-street-view-control': boolean
  'location-behavior-zoom-control': boolean
  'location-behavior-info-bubble': string

  // Actions
  'location-action-on-click': BuilderActions
}

export interface IMapComponent extends IBuilderComponent {
  componentId: 'map'
  groupId: 'visual'
  settings: IMapComponentSettings
}

export interface AiMapInput {
  coordinate?: string // Format: "lat,lng" e.g., "37.8199109,-122.4785598"
  marker?: string // Icon name or URL
  header?: string // Map header text
  subheader?: string // Map subheader text
  description?: string // Map description text
  avatar?: string // Avatar image URL
  image?: string // Background image URL
  infoBubbleColor?: string // e.g., "primary-90"
  markerColor?: string // e.g., "primary-90"
  width?: string // Format: "w:100%" or "w:600px" or "w:100% de:w:600px"
  height?: string // Format: "h:400px" or "h:400px de:h:600px"
  alignment?: string // Format: "parent" or "center" or "parent de:center"
  cornerRadius?: string // Format: "none" or "small" or "medium" or "large"
  margin?: string // Format: "m:10px" or "mt:10px mb:10px"
  zoomLevel?: string // "near", "medium", or "far"
  showMapTypeControl?: boolean
  showFullscreenControl?: boolean
  showStreetViewControl?: boolean
  showZoomControl?: boolean
  infoBubbleStyle?: string // "default", "style-1", "style-2", "style-3", "style-4"
  onBubbleClickAction?: BuilderActions[]
}

const MAP_DEFAULT_TEMPLATE = mapTemplate as unknown as IMapComponentSettings

export class MapComp {
  getTemplate(): IMapComponentSettings {
    return MAP_DEFAULT_TEMPLATE
  }

  /**
   * format map component settings to AI map input
   */
  format(settings: IMapComponentSettings): AiMapInput {
    const result: AiMapInput = {}

    // Data - Coordinate (from VariableSelectValue)
    if (settings['location-data-address']) {
      const coordinate = formatFromVariableSelect(settings['location-data-address'])
      if (coordinate && coordinate.startsWith('text:')) {
        result.coordinate = coordinate.substring(5)
      } else {
        result.coordinate = coordinate
      }
    }

    // Data - Marker (from VariableSelectValue with Icon flag)
    if (settings['location-data-marker']) {
      const marker = formatFromVariableSelect(settings['location-data-marker'])
      if (marker && marker.startsWith('icon:')) {
        result.marker = marker.substring(5)
      } else {
        result.marker = marker
      }
    }

    // Data - Header
    if (settings['location-data-header']) {
      const header = formatFromVariableText(settings['location-data-header'])
      result.header = header.length > 0 ? header.join(' ') : undefined
    }

    // Data - Subheader
    if (settings['location-data-subheader']) {
      const subheader = formatFromVariableText(settings['location-data-subheader'])
      result.subheader = subheader.length > 0 ? subheader.join(' ') : undefined
    }

    // Data - Description
    if (settings['location-data-description']) {
      const description = formatFromVariableText(settings['location-data-description'])
      result.description = description.length > 0 ? description.join(' ') : undefined
    }

    // Data - Avatar (from VariableSelectValue)
    if (settings['location-data-avatar']) {
      const avatar = formatFromVariableSelect(settings['location-data-avatar'])
      if (avatar && avatar.startsWith('text:')) {
        result.avatar = avatar.substring(5)
      } else {
        result.avatar = avatar
      }
    }

    // Data - Image (from VariableSelectValue)
    if (settings['location-data-image']) {
      const image = formatFromVariableSelect(settings['location-data-image'])
      if (image && image.startsWith('text:')) {
        result.image = image.substring(5)
      } else {
        result.image = image
      }
    }

    // Design - Info Bubble Color
    if (settings['location-design-scheme']) {
      result.infoBubbleColor = formatColorVariantFormat(settings['location-design-scheme'])
    }

    // Design - Marker Color
    if (settings['location-design-color-marker']) {
      result.markerColor = formatColorVariantFormat(settings['location-design-color-marker'])
    }

    // Design - Width (handle OverflowSettings)
    if (settings['location-design-width']) {
      const width = formatWidthFormat(settings['location-design-width'])
      if (width) {
        result.width = width
      }
    }

    // Design - Height
    if (settings['location-design-height']) {
      result.height = formatResponsiveValue(settings['location-design-height'], (val) => {
        if (Array.isArray(val) && val.length > 0) {
          return `h:${val[0]}px`
        }
        return 'h:400px'
      })
    }

    // Design - Alignment
    if (settings['location-design-alignment']) {
      result.alignment = formatComponentAlignmentFormat(settings['location-design-alignment'])?.replace(/^h:/, '') || undefined
    }

    // Design - Corner Radius
    if (settings['location-design-shape']) {
      result.cornerRadius = formatResponsiveCornerRadiusFormat(settings['location-design-shape'])
    }

    // Design - Margin (handle OverflowSettings)
    if (settings['location-design-margin']) {
      const margin = formatSpacingFormat(settings['location-design-margin'])
      if (margin) {
        result.margin = margin
      }
    }

    // Design - Zoom Level
    if (settings['location-design-zoom-level']) {
      result.zoomLevel = settings['location-design-zoom-level']
    }

    // Behavior - Map Type Control
    if (settings['location-behavior-map-type-control'] !== undefined) {
      result.showMapTypeControl = settings['location-behavior-map-type-control']
    }

    // Behavior - Fullscreen Control
    if (settings['location-behavior-fullscreen-control'] !== undefined) {
      result.showFullscreenControl = settings['location-behavior-fullscreen-control']
    }

    // Behavior - Street View Control
    if (settings['location-behavior-street-view-control'] !== undefined) {
      result.showStreetViewControl = settings['location-behavior-street-view-control']
    }

    // Behavior - Zoom Control
    if (settings['location-behavior-zoom-control'] !== undefined) {
      result.showZoomControl = settings['location-behavior-zoom-control']
    }

    // Behavior - Info Bubble Style
    if (settings['location-behavior-info-bubble']) {
      result.infoBubbleStyle = settings['location-behavior-info-bubble']
    }

    return result
  }

  /**
   * parse AI map input to map component settings
   */
  parse(input: AiMapInput): IMapComponentSettings {
    const result = deepMerge(MAP_DEFAULT_TEMPLATE, {})

    // Data - Coordinate
    if (input.coordinate) {
      result['location-data-address'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: input.coordinate,
      }
    }

    // Data - Marker
    if (input.marker) {
      result['location-data-marker'] = {
        flag: 8, // Icon flag
        id: uuidv4(),
        value: input.marker,
      }
    }

    // Data - Header
    if (input.header) {
      result['location-data-header'] = parseToVariableText(input.header)
    }

    // Data - Subheader
    if (input.subheader) {
      result['location-data-subheader'] = parseToVariableText(input.subheader)
    }

    // Data - Description
    if (input.description) {
      result['location-data-description'] = parseToVariableText(input.description)
    }

    // Data - Avatar
    if (input.avatar) {
      result['location-data-avatar'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: input.avatar,
      }
    }

    // Data - Image
    if (input.image) {
      result['location-data-image'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: input.image,
      }
    }

    // Design - Info Bubble Color
    if (input.infoBubbleColor) {
      const color = input.infoBubbleColor as ColorVariant
      result['location-design-scheme'] = {
        light: color,
        dark: color,
      }
    }

    // Design - Marker Color
    if (input.markerColor) {
      const color = input.markerColor as ColorVariant
      result['location-design-color-marker'] = {
        light: color,
        dark: color,
      }
    }

    // Design - Width
    if (input.width) {
      const widthParsed = parseWidthFormat(input.width)
      result['location-design-width'] = {
        ...widthParsed,
        overflowSetting: result['location-design-width'].overflowSetting,
      }
    }

    // Design - Height
    if (input.height) {
      // Parse height format: "h:400px" or responsive "h:400px de:h:600px"
      const heightStr = input.height.replace(/h:/g, '')
      result['location-design-height'] = parseResponsiveFormat(heightStr, (v) => [
        parseInt(v.replace('px', '')) || 400,
      ])
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['location-design-alignment'] = alignmentParsed.hAlign
      }
    }

    // Design - Corner Radius
    if (input.cornerRadius) {
      result['location-design-shape'] = parseCornerRadiusFormat(input.cornerRadius)
    }

    // Design - Margin
    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      result['location-design-margin'] = {
        ...marginParsed,
        overflowSetting: result['location-design-margin'].overflowSetting,
      }
    }

    // Design - Zoom Level
    if (input.zoomLevel) {
      result['location-design-zoom-level'] = input.zoomLevel
    }

    // Behavior - Map Type Control
    if (input.showMapTypeControl !== undefined) {
      result['location-behavior-map-type-control'] = input.showMapTypeControl
    }

    // Behavior - Fullscreen Control
    if (input.showFullscreenControl !== undefined) {
      result['location-behavior-fullscreen-control'] = input.showFullscreenControl
    }

    // Behavior - Street View Control
    if (input.showStreetViewControl !== undefined) {
      result['location-behavior-street-view-control'] = input.showStreetViewControl
    }

    // Behavior - Zoom Control
    if (input.showZoomControl !== undefined) {
      result['location-behavior-zoom-control'] = input.showZoomControl
    }

    // Behavior - Info Bubble Style
    if (input.infoBubbleStyle) {
      result['location-behavior-info-bubble'] = input.infoBubbleStyle
    }

    // Actions - On Bubble Click
    if (input.onBubbleClickAction) {
      // @ts-ignore
      result['location-action-on-click'] = input.onBubbleClickAction
    }

    return result
  }
}

